import type { Evidence, MandateView, OccurrenceView, PolicyEvaluated, RunDue } from "../api/types.js";
import { clock, formatMoney, sgDay } from "../lib/format.js";
import type { JRow, Part } from "./judge-model.js";
import { activeSoonest } from "./mandates-model.js";
import { mandateAmount, mandateName, ordinal, weekdayName } from "./schedule-words.js";

/**
 * DCTA-209 F62 (09-scheduled.md §2.6, row 31) — judge mode's view of the scheduler: the Scheduled payments panel (route 1)
 * and, for each press of "Run due payments now", what ran (route 4) — each payment's mandate hash, its checks read from its
 * own policy trace (the `policy_evaluated` record the scheduler wrote on the signing bundle, read with the evidence route),
 * its record's place in the chain. Judge register (mandate, hash, record, model) under a `.judge` root: never customer copy.
 * Everything is the bank's; nothing here decides whether a check passed.
 */
const t = (s: string): Part => ({ kind: "text", t: s });
const m = (s: string): Part => ({ kind: "mono", t: s });
const id = (s: string): Part => ({ kind: "id", t: s });
const ok = (s: string): Part => ({ kind: "pass", t: s });
const bad = (s: string): Part => ({ kind: "fail", t: s });
/** The mandate id as judge mode shows it: "mdt·" + its first 8 hex; the full hash on tap. */
const mdt = (mandateId: string): Part => ({ kind: "hash", full: mandateId, label: "Mandate", short: `mdt·${mandateId.slice(0, 8)}` });

const cadence = (v: MandateView): string => {
  const s = v.schedule;
  if (s.cadence === "monthly") return `monthly on the ${ordinal(s.day_of_month ?? "")}`;
  if (s.cadence === "weekly") return `weekly on ${s.weekday === undefined ? "a weekday" : weekdayName(s.weekday)}`;
  return `once on ${s.first_on}`;
};

export interface SchedPanel {
  readonly active: number;
  readonly rows: readonly JRow[];
}

/** 31a: how many are active, the next due (bank time, SGT), one row per active schedule. */
export function schedPanel(list: readonly MandateView[]): SchedPanel {
  const byNext = activeSoonest(list);
  if (byNext.length === 0) return { active: 0, rows: [{ k: "Scheduled", v: [t("none active · sign a schedule card in the chat")] }] };
  const next = byNext[0] as MandateView;
  const rows: JRow[] = [
    {
      k: "Next due",
      v: [m(`${next.next_due_on ?? next.schedule.first_on} ${next.schedule.due_at_local} SGT`)],
      small: `${mandateName(next)} · ${mandateAmount(next)} · ${next.schedule.cadence}`,
    },
  ];
  for (const v of byNext) {
    const made = Number(v.payments_made);
    const skipped = Number(v.payments_skipped);
    const ran = made + skipped;
    rows.push({
      k: mandateName(v),
      v: [mdt(v.mandate_id)],
      small: `${cadence(v)} · next ${v.next_due_on ?? v.schedule.first_on} · ${String(ran)} of at most ${v.schedule.occurrences_max} run${ran === 0 ? "" : `: ${String(made)} executed, ${String(skipped)} skipped`}`,
    });
  }
  return { active: byNext.length, rows };
}

/** The payments' traces on a signing bundle's evidence, by `{mandate_id}:{occurrence}`. */
export function tracesOf(e: Evidence | undefined): ReadonlyMap<string, PolicyEvaluated> {
  const out = new Map<string, PolicyEvaluated>();
  for (const r of e?.parsed.policy_evaluated ?? []) if (r.mandate_id !== undefined && r.occurrence !== undefined) out.set(`${r.mandate_id}:${r.occurrence}`, r);
  return out;
}

/** F62's groups, in the order the checks run: each names the rules that decide it. */
const GROUPS: readonly { readonly name: string; readonly has: (rule: string) => boolean }[] = [
  { name: "KYC", has: (r) => r === "POL-KYC-GATE" || r === "POL-KYC-UNKNOWN" },
  { name: "not locked", has: (r) => r === "POL-PROTECTION-ACTIVE" },
  { name: "balance", has: (r) => r === "POL-BAL-SUFFICIENT" },
  { name: "limits and velocity", has: (r) => ["POL-LIMIT-TXN", "POL-LIMIT-DAILY", "POL-LIMIT-DAILY-CEILING", "POL-STRUCTURING"].includes(r) || r.startsWith("POL-VELOCITY-") },
  { name: "payee saved, no hold", has: (r) => r === "POL-SCHED-DESTINATION" || r === "POL-HOLD-LARGE-OUTFLOW" },
  { name: "no scam hold", has: (r) => r.startsWith("POL-SCAM-") || r === "POL-SCHED-SCAM" },
  { name: "amount = signed", has: (r) => r === "POL-SCHED-EXEC" || r === "POL-SCHED-CAP" },
];

/**
 * DCTA-231 V4: rules that ALLOW (mock-bank-api, action "allow"). In the bank's trace `fail` means a rule FIRED (lab/dcta
 * policy/types.ts) — for these it means it applied: POL-OWN-ACCOUNT found the destination is the customer's own account and
 * exempted the move from third-party limits. Said as applied (✓ with its id), never as a failed check.
 */
const APPLIED: readonly { readonly name: string; readonly rule: string }[] = [{ name: "own account", rule: "POL-OWN-ACCOUNT" }];
const allows = (rule: string): boolean => APPLIED.some((a) => a.rule === rule);

/**
 * F62's Checks for one payment. With its trace: a group is ✓ when each of its rules passed or did not apply, ✗ with the rule
 * ids that fired; a payment that reached the engine had its destination found and its amount within what was signed (the
 * scheduler checks both before it asks the engine). A skip shows the groups up to the one that stopped it. Without a trace
 * (skipped before the engine: the destination gone, over the cap, a missed day): the skip's own rule ids, in their group.
 * DCTA-231 V4: an allow rule that fired (`APPLIED`) is said after the groups as applied — never in a ✗.
 */
export function checksOf(o: Pick<OccurrenceView, "state" | "rule_ids">, trace: PolicyEvaluated | undefined, amount: string): Part[] {
  const sep = (): Part => t(" · ");
  if (trace?.evaluated === undefined) {
    const ids = (o.rule_ids ?? []).filter((r) => r !== "POL-SCHED-EXEC" && !allows(r));
    if (o.state === "executed" || ids.length === 0) return [t("no trace on the record")];
    const group = GROUPS.find((g) => ids.some((r) => g.has(r)));
    return [...(group === undefined ? [] : [t(`${group.name} `)]), bad("✗"), ...ids.flatMap((r) => [t(" "), id(r)])];
  }
  const evaluated = trace.evaluated;
  const parts: Part[] = [];
  for (const g of GROUPS) {
    const fired = evaluated.filter((e) => g.has(e.rule_id) && e.result === "fail").map((e) => e.rule_id);
    if (parts.length > 0) parts.push(sep());
    if (fired.length === 0) {
      parts.push(t(g.name === "amount = signed" && o.state === "executed" ? `${g.name} ${amount} ` : `${g.name} `), ok("✓"));
      continue;
    }
    parts.push(t(`${g.name} `), bad("✗"), ...fired.flatMap((r) => [t(" "), id(r)]));
    // A skip stops at what stopped it: the rest were not what decided it.
    if (o.state !== "executed") return parts;
  }
  // DCTA-231 V4: an allow rule that fired applied — said as such, with its id.
  // MUTATION TARGET: without it an executed own-account top-up reads "other ✗ POL-OWN-ACCOUNT" (prod-verify V4).
  for (const a of APPLIED) if (evaluated.some((e) => e.rule_id === a.rule && e.result === "fail")) parts.push(sep(), t(`${a.name} `), ok("✓"), t(" "), id(a.rule));
  // A rule that fired outside every group (one this list does not know yet) is shown — never hidden behind a row of ✓.
  const stray = evaluated.filter((e) => e.result === "fail" && !allows(e.rule_id) && !GROUPS.some((g) => g.has(e.rule_id))).map((e) => e.rule_id);
  if (stray.length > 0) parts.push(sep(), t("other "), bad("✗"), ...stray.flatMap((r) => [t(" "), id(r)]));
  return parts;
}

export interface RunStage {
  readonly n: number;
  readonly title: string;
  readonly state: "executed" | "skipped" | "claimed";
  readonly rows: readonly JRow[];
}

export interface RunPanel {
  /** "bank time 2026-11-01 09:00 · simulated". */
  readonly at: string;
  readonly stages: readonly RunStage[];
  readonly empty?: string;
}

export interface RunContext {
  readonly mandates: readonly MandateView[];
  readonly traces: ReadonlyMap<string, PolicyEvaluated>;
  /** A record's place in the chain, by its audit ref (`GET /v1/audit/records`). */
  readonly seqOf: (auditRef: string) => string | undefined;
  readonly chainOk?: boolean;
}

/** 31d: one press — the bank time it moved to and one numbered stage per payment that ran. */
export function runPanel(r: RunDue, ctx: RunContext): RunPanel {
  const at = `bank time ${sgDay(r.now)} ${clock(r.now)} · simulated`;
  if (r.ran.length === 0) return { at, stages: [], empty: "Nothing was due at this time." };
  const stages = r.ran.map((o, i): RunStage => {
    const v = ctx.mandates.find((x) => x.mandate_id === o.mandate_id);
    const figure = o.amount === undefined ? (v === undefined ? "" : mandateAmount(v)) : formatMoney(o.amount.value, o.amount.currency);
    const who = v === undefined ? `mdt·${o.mandate_id.slice(0, 8)}` : mandateName(v);
    const max = v?.schedule.occurrences_max;
    const mandateRow: JRow = {
      k: "Mandate",
      v: [mdt(o.mandate_id)],
      small: [`payment ${o.seq}${max === undefined ? "" : ` of at most ${max}`}`, `due ${o.due_on}`, `line “${o.line}”`, ...(o.ledger_ref === undefined ? [] : [`ledger ${o.ledger_ref}`])].join(" · "),
    };
    const next = v?.next_due_on;
    const checks: JRow = {
      k: "Checks",
      v: checksOf(o, ctx.traces.get(`${o.mandate_id}:${o.seq}`), figure),
      ...(o.state === "executed" ? {} : { small: `skipped: nothing taken, nothing partial${next === undefined ? "" : ` · next ${next}`}` }),
    };
    const seq = o.audit_ref === undefined ? undefined : ctx.seqOf(o.audit_ref);
    const record: JRow = {
      k: "Record",
      v: [m("step_executed"), t(` · ${o.state}${seq === undefined ? "" : ` · #${seq}`}${ctx.chainOk === undefined ? "" : ctx.chainOk ? " · chain ✓" : " · chain ✗"}`)],
      small: "no model was called for this payment",
    };
    return { n: i + 1, title: `${who}${figure === "" ? "" : ` · ${figure}`}`, state: o.state, rows: [mandateRow, checks, record] };
  });
  return { at, stages };
}
